import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { settingsConfigurationService } from 'services';
import { errorTranslations } from 'translations';

const RecaptchaV3 = forwardRef(({ onChange, onExpired }, ref) => {
    const [hasError, setHasError] = useState(false);
    const { googleRecaptchaV3Key } = settingsConfigurationService.get();
    const componentRef = useRef(null);

    RecaptchaV3.displayName = 'RecaptchaV3';

    const [captchaResolveToken, setCaptchaResolveToken] = useState('');

    const reGenerageCaptcha = async () => {
        if (executeRecaptcha) {
            const token = await executeRecaptcha();

            setCaptchaResolveToken(token);
            onChange(token);
        }
    };

    // Expose async methods to parent
    useImperativeHandle(ref, () => ({
        reGenerageCaptcha,
    }));

    const executeRecaptcha = async () => {
        return new Promise((resolve, reject) => {
            const grecaptchaClient = window.grecaptcha?.enterprise || window.grecaptcha;

            if (!grecaptchaClient) {
                return reject('reCAPTCHA not loaded');
            }

            grecaptchaClient.ready(async () => {
                await grecaptchaClient
                    .execute(googleRecaptchaV3Key, { action: 'submit' })
                    .then((token) => resolve(token))
                    .catch((err) => reject(err));
            });
        });
    };

    useEffect(() => {
        // Create a <script> tag
        const script = document.createElement('script');

        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${googleRecaptchaV3Key}`;
        script.async = true;
        script.defer = true;

        script.onload = async () => {
            if (executeRecaptcha) {
                const token = await executeRecaptcha();

                setCaptchaResolveToken(token);
                onChange(token);
            }
        };

        script.onerror = () => {
            setHasError(true);
            onExpired();
        };

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [googleRecaptchaV3Key]);

    // // ---------------------------------------------
    // // 🚀 AUTO-REFRESH RECAPTCHA TOKEN EVERY 2 MINUTES
    // // ---------------------------------------------
    // useEffect(() => {
    //     const interval = setInterval(async () => {
    //         try {
    //             const token = await executeRecaptcha();

    //             setCaptchaResolveToken(token);
    //             onChange(token);
    //         } catch (e) {
    //             setHasError(true);
    //             // eslint-disable-next-line no-console
    //             console.error('Recaptcha auto-refresh failed:', e);
    //         }
    //     }, 2 * 60 * 900); // 2 minutes

    //     return () => clearInterval(interval);
    // }, [googleRecaptchaV3Key]);
    // // ---------------------------------------------

    if (hasError) {
        return <FormattedMessage {...errorTranslations.recaptchaLoadError} />;
    }

    return (
        <input
            type="hidden"
            id="g-recaptcha-response"
            name="googleRecaptchaResponse"
            ref={componentRef}
            value={captchaResolveToken}
        />
    );
});

RecaptchaV3.propTypes = {
    regenerateToken: PropTypes.bool,
    onChange: PropTypes.func,
    onExpired: PropTypes.func,
};

export default RecaptchaV3;
